import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { FaWallet, FaBuilding, FaEdit, FaSyncAlt, FaUser, FaDollarSign } from 'react-icons/fa'; // Import icons
import { Oval } from "react-loader-spinner"; // Import loader
import { StandardURL } from "../App";


// Helper function for currency formatting (improves UI readability)
const formatCurrency = (amount) => {
    const numericAmount = parseFloat(amount) || 0;
    // Assuming KES for demonstration; change 'en-KE' and 'KES' if locale/currency differs
    return new Intl.NumberFormat('en-KE', { 
        style: 'currency', 
        currency: 'KES', 
        minimumFractionDigits: 2,
    }).format(numericAmount).replace('KES', 'Ksh ');
};

const UpdateLandlordBalance = () => {
    const [balances, setBalances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editBalanceId, setEditBalanceId] = useState(null); // Corresponds to property_id in the API
    const [balanceAmount, setBalanceAmount] = useState("");
    const [currentPropertyId, setCurrentPropertyId] = useState(null); // Used for display

    useEffect(() => {
        fetchPropertyBalances();
    }, []);

    const fetchPropertyBalances = async () => {
        setLoading(true);
        const token = localStorage.getItem("token");
        try {
            const response = await axios.get(
                `${StandardURL}/iNyumba/balance/viewlandlordbalances`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            setBalances(response.data.response);
            toast.info(`Fetched ${response.data.response.length} landlord balances.`);
        } catch (error) {
            const errorMessage = error.response?.data?.response || "Failed to fetch landlord balances.";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (balance) => {
        // As per original logic, we use balance.property_id for the ID to be patched
        setEditBalanceId(balance.property_id); 
        // Ensure input is a string
        setBalanceAmount(String(balance.balance)); 
        setCurrentPropertyId(balance.property_id);
    };

    const handleUpdate = async () => {
        if (!editBalanceId) {
            toast.error("No property balance selected for update");
            return;
        }
        
        const token = localStorage.getItem("token");
        setIsSubmitting(true);

        try {
            const response = await axios.patch(
                `${StandardURL}/iNyumba/balance/updatelandlordbalance/${editBalanceId}`,
                {
                    balance_amount: balanceAmount,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            toast.success(response.data.response);
        } catch (error) {
            const errorMessage = error.response?.data?.response || "Failed to update balance.";
            toast.error(errorMessage);
        } finally {
            setEditBalanceId(null); // Hide the edit section
            setIsSubmitting(false);
            fetchPropertyBalances(); // Refresh the balance list
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <Oval
                    height={50}
                    width={50}
                    color="#06b6d4"
                    secondaryColor="#67e8f9"
                    ariaLabel="loading-indicator"
                />
                <p className="ml-3 text-lg text-gray-700">Fetching landlord balances...</p>
            </div>
        );
    }

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                    <div className="max-w-4xl mx-auto bg-white shadow-2xl rounded-xl p-6 lg:p-8">
                        
                        {/* Header */}
                        <h1 className="text-3xl font-extrabold text-gray-900 mb-6 flex items-center border-b pb-4">
                            <FaBuilding className="text-cyan-600 mr-3" /> Landlord/Property Balances
                        </h1>

                        <div className="flex justify-end mb-4">
                             <button
                                onClick={fetchPropertyBalances}
                                className="flex items-center bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-200 text-sm"
                            >
                                <FaSyncAlt className="mr-2" /> Refresh Balances
                            </button>
                        </div>
                        
                        {/* Table of Balances */}
                        <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance ID</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property ID</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner ID</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Current Balance</th>
                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {balances.length > 0 ? (
                                        balances.map((balance, index) => (
                                            <tr key={balance.property_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{balance.balance_id || 'N/A'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-cyan-700 flex items-center">
                                                    <FaBuilding className="mr-2 text-cyan-500 w-4 h-4" />
                                                    {balance.property_id}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 flex items-center">
                                                    <FaUser className="mr-2 text-blue-500 w-4 h-4" />
                                                    {balance.owner_id}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-right">
                                                    <span className={parseFloat(balance.balance) >= 0 ? 'text-green-600' : 'text-blue-600'}>
                                                        {formatCurrency(balance.balance)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                    <button
                                                        onClick={() => handleEdit(balance)}
                                                        className="text-cyan-600 hover:text-cyan-800 p-2 rounded-full hover:bg-cyan-50 transition flex items-center justify-center mx-auto"
                                                        title="Edit Property Balance"
                                                    >
                                                        <FaEdit className="w-5 h-5" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center py-6 text-gray-500">
                                                No landlord property balances found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Inline Edit Form (Conditional Rendering) */}
                        {editBalanceId && (
                            <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg shadow-lg">
                                <h2 className="text-xl font-bold text-blue-800 mb-4 flex items-center">
                                    <FaEdit className="mr-2" /> Adjust Balance for Property ID: <span className="ml-2 text-blue-900">{currentPropertyId}</span>
                                </h2>
                                <p className="text-sm text-blue-700 mb-4">Warning: Manually setting this balance will affect the landlord's funds related to this property. Use with caution.</p>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-blue-800 mb-2 flex items-center">
                                        <FaDollarSign className="mr-2" /> New Balance Amount
                                    </label>
                                    <input
                                        type="number" // Changed to number for better mobile and validation UX
                                        value={balanceAmount}
                                        onChange={(e) => setBalanceAmount(e.target.value)}
                                        className="w-full px-4 py-2 border border-blue-300 rounded-lg shadow-sm focus:ring-cyan-500 focus:border-cyan-500 transition"
                                        placeholder="Enter the new total balance"
                                    />
                                </div>
                                <div className="flex space-x-3">
                                    <button
                                        onClick={handleUpdate}
                                        disabled={isSubmitting}
                                        className={`flex-1 inline-flex items-center justify-center font-bold py-2.5 px-6 rounded-lg shadow-md transition duration-200 text-sm 
                                            ${isSubmitting
                                                ? "bg-gray-400 cursor-not-allowed"
                                                : "bg-green-600 hover:bg-green-700 text-white"
                                            } disabled:opacity-70`}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Oval height={18} width={18} color="white" secondaryColor="#ffffff" strokeWidth={5} />
                                                <span className="ml-2">Updating...</span>
                                            </>
                                        ) : (
                                            "Confirm New Balance"
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setEditBalanceId(null)}
                                        disabled={isSubmitting}
                                        className="flex-1 bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 font-bold py-2.5 px-6 rounded-lg shadow-md transition duration-200 text-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </AuthGuard>
        </EmailGuard>
    );
};

export default UpdateLandlordBalance;